import { EXPEDITION_ROOMS, UNLOCKS, salvageFor } from './expedition';

export interface Progress {
  salvage: number;
  unlocks: string[];
  bestRoom: number;
  runs: number;
  clears: number;
}

export type ProgressOperation = { id: string } & (
  | { type: 'legacy'; progress: Progress }
  | { type: 'start' }
  | { type: 'finish'; cleared: number; reached: number; finished: boolean }
  | { type: 'buy'; code: string }
);

export const blankProgress = (): Progress => ({ salvage: 0, unlocks: [], bestRoom: 0, runs: 0, clears: 0 });
const int = (v: unknown, max = 1_000_000): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= max;

export function isProgress(v: unknown): v is Progress {
  const p = v as Progress | null;
  return !!p && int(p.salvage) && int(p.bestRoom, 1000) && int(p.runs) && int(p.clears) &&
    Array.isArray(p.unlocks) && p.unlocks.length <= UNLOCKS.length &&
    p.unlocks.every(code => UNLOCKS.some(u => u.code === code));
}

export function isProgressOperation(v: unknown): v is ProgressOperation {
  const op = v as ProgressOperation | null;
  if (!op || typeof op.id !== 'string' || !/^[\w:-]{1,100}$/.test(op.id)) return false;
  switch (op.type) {
    case 'legacy': return isProgress(op.progress);
    case 'start': return true;
    case 'buy': return UNLOCKS.some(u => u.code === op.code);
    case 'finish': return int(op.cleared, EXPEDITION_ROOMS) && int(op.reached, EXPEDITION_ROOMS) &&
      op.cleared <= op.reached && typeof op.finished === 'boolean' &&
      (!op.finished || op.cleared === EXPEDITION_ROOMS);
    default: return false;
  }
}

/** The same purchase and reward rules run offline and in the account transaction. */
export function applyProgress(p: Progress, op: ProgressOperation): { progress: Progress; accepted: boolean } {
  const next = { ...p, unlocks: [...p.unlocks] };
  switch (op.type) {
    case 'legacy': {
      // Old saves contain balances, not earning histories. Preserve the larger
      // lifetime total and all purchases; adding balances would refund spending
      // whenever a stale browser first upgraded. New rewards have unique IDs.
      const cost = (codes: string[]) => UNLOCKS.reduce((sum, u) => sum + (codes.includes(u.code) ? u.cost : 0), 0);
      next.unlocks = [...new Set([...p.unlocks, ...op.progress.unlocks])].sort();
      next.salvage = Math.max(0, Math.max(p.salvage + cost(p.unlocks),
        op.progress.salvage + cost(op.progress.unlocks)) - cost(next.unlocks));
      next.bestRoom = Math.max(p.bestRoom, op.progress.bestRoom);
      next.runs = Math.max(p.runs, op.progress.runs);
      next.clears = Math.max(p.clears, op.progress.clears);
      break;
    }
    case 'start': next.runs++; break;
    case 'finish':
      next.salvage += salvageFor(op.cleared, op.finished);
      next.bestRoom = Math.max(p.bestRoom, op.reached);
      next.clears += Number(op.finished);
      break;
    case 'buy': {
      const unlock = UNLOCKS.find(u => u.code === op.code)!;
      if (next.unlocks.includes(op.code)) break;
      if (next.salvage < unlock.cost) return { progress: p, accepted: false };
      next.salvage -= unlock.cost;
      next.unlocks.push(op.code);
      next.unlocks.sort();
    }
  }
  next.salvage = Math.min(1_000_000, next.salvage);
  next.runs = Math.min(1_000_000, next.runs);
  next.clears = Math.min(1_000_000, next.clears);
  return { progress: next, accepted: true };
}
